import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { ErrorBanner } from '../components/ui/ErrorBanner';
import { PageHeader } from '../components/ui/PageHeader';
import { Select } from '../components/ui/Select';
import { SkeletonList } from '../components/ui/Skeleton';
import {
  PROJEKT_STATUS_BADGE,
  PROJEKT_STATUS_LABEL,
  projekteApi,
  type Projekt,
  type ProjektStatus,
} from '../lib/projekte-api';
import { requireAccess } from '../lib/guards';
import { formatGermanDateFromTimestamp } from '../lib/date-format';

export const Route = createFileRoute('/projekte/')({
  beforeLoad: () => requireAccess('/api/v1/projekte'),
  component: ProjekteIndexPage,
});

const STATUS_FILTER: Array<{ value: '' | ProjektStatus; label: string }> = [
  { value: '', label: 'Alle Status' },
  { value: 'offen', label: 'Offen' },
  { value: 'in_arbeit', label: 'In Arbeit' },
  { value: 'abgeschlossen', label: 'Abgeschlossen' },
  { value: 'storniert', label: 'Storniert' },
];

export function ProjekteIndexPage(): JSX.Element {
  const [statusFilter, setStatusFilter] = useState<'' | ProjektStatus>('');

  const projekteQ = useQuery({
    queryKey: ['projekte', 'list'],
    queryFn: () => projekteApi.list(),
  });

  const items = projekteQ.data?.items ?? [];
  const filtered = items.filter((p) => statusFilter === '' || p.status === statusFilter);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Projekte"
        description="Schadens- und Koordinations-Container, die mehrere Aufträge desselben Kunden bündeln."
        actions={
          <Link
            to="/projekte/neu"
            className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
          >
            Neues Projekt
          </Link>
        }
      />

      {projekteQ.error ? <ErrorBanner error={projekteQ.error as Error} /> : null}

      <div className="flex flex-wrap gap-3">
        <label className="min-w-[160px] flex-1">
          <span className="mb-1 block text-sm font-medium text-slate-700">Status</span>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as '' | ProjektStatus)}
          >
            {STATUS_FILTER.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </label>
      </div>

      {projekteQ.isLoading ? (
        <SkeletonList />
      ) : filtered.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">
          Keine Projekte in dieser Auswahl.
        </div>
      ) : (
        <ul className="space-y-3">
          {filtered.map((p) => (
            <ProjektCard key={p.id} projekt={p} />
          ))}
        </ul>
      )}
    </div>
  );
}

function ProjektCard({ projekt }: { projekt: Projekt }): JSX.Element {
  return (
    <li>
      <Link
        to="/projekte/$id"
        params={{ id: projekt.id }}
        className="block rounded-lg border border-slate-200 bg-white p-4 hover:border-slate-300 hover:shadow-sm"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="truncate text-base font-medium text-slate-900">
              {projekt.bezeichnung}
            </div>
            <div className="mt-1 text-xs text-slate-500">
              {projekt.schadennummer ? `Schaden ${projekt.schadennummer} · ` : ''}
              angelegt {formatGermanDateFromTimestamp(projekt.createdAt)}
            </div>
          </div>
          <span
            className={`shrink-0 rounded-md border px-2 py-1 text-xs font-medium ${PROJEKT_STATUS_BADGE[projekt.status]}`}
          >
            {PROJEKT_STATUS_LABEL[projekt.status]}
          </span>
        </div>
      </Link>
    </li>
  );
}
